
import React, { useState } from 'react';
import axios from 'axios';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    LinearProgress,
    Alert,
    Box
} from '@mui/material';

import {
    showmodal,
    ajout,
    addOne
} from './objectifSlice';

// Adapter ces imports selon ton projet.
const apiUrl = process.env.REACT_APP_API_URL;

export default function Ajouter() {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const objectifData = useSelector(
        state => state.objectifsData
    );

    const { id, apitoken } = useSelector(
        state => state.userData
    );

    const [valPrevendeur, setValPrevendeur] = useState('');
    const [valMarque, setValMarque] = useState('');
    const [valObjectif, setValObjectif] = useState('');

    const [chargement, setChargement] = useState(false);
    const [erreur, setErreur] = useState('');

    const fermer = () => {

        if (chargement) {
            return;
        }

        dispatch(showmodal(false));

        setValPrevendeur('');
        setValMarque('');
        setValObjectif('');
        setErreur('');
    };

    const enregistrer = async () => {

        if (chargement) {
            return;
        }

        setErreur('');

        // ---------------------------------------------------
        // Validation
        // ---------------------------------------------------

        if (!valPrevendeur) {
            setErreur('Veuillez sélectionner un prévendeur.');
            return;
        }

        if (!valMarque) {
            setErreur('Veuillez sélectionner une marque.');
            return;
        }

        const montant = Number(valObjectif);

        if (
            valObjectif === '' ||
            !Number.isFinite(montant) ||
            montant <= 0
        ) {
            setErreur(
                'Veuillez saisir un objectif CA supérieur à zéro.'
            );
            return;
        }

        // ---------------------------------------------------
        // Récupération des objets sélectionnés
        // ---------------------------------------------------

        const prevendeur = objectifData.listPrevendeur.find(
            element => element._id === valPrevendeur
        );

        const marque = objectifData.listMarque.find(
            element => element._id === valMarque
        );

        if (!prevendeur || !marque) {
            setErreur(
                'Prévendeur ou marque introuvable.'
            );
            return;
        }

        // ---------------------------------------------------
        // Données attendues par ObjectifModel
        // ---------------------------------------------------

        const data = {
            prevendeur: {
                id: prevendeur._id,
                nom: prevendeur.nom
            },

            marque: {
                id: marque._id,
                designation: marque.designation
            },

            objectif_ca: montant
        };

        // ---------------------------------------------------
        // Enregistrement
        // ---------------------------------------------------

        try {

            setChargement(true);

            const response = await axios.post(
                `${apiUrl}new/objectif/${id}/${apitoken}`,
                data
            );

            const resultat = response.data;

            if (resultat.status === 'duplicate') {

                setErreur(
                    'Un objectif existe déjà pour ce prévendeur et cette marque.'
                );

                return;
            }

            if (resultat.status !== 'ok') {

                setErreur(
                    "Impossible d'enregistrer cet objectif."
                );

                return;
            }

            // Le backend renvoie le même ID en cas
            // de réactivation d'un objectif supprimé.
            const nouvelObjectif = {
                _id: resultat.id,

                prevendeur: data.prevendeur,
                marque: data.marque,

                objectif_ca: montant
            };

            dispatch(addOne(nouvelObjectif));
            dispatch(ajout(true));

            fermerApresSucces();

        } catch (error) {

            console.error(error);

            if (error.response?.status === 401) {

                navigate('/');

            } else if (error.response?.status === 422) {

                setErreur(
                    'Les données envoyées sont invalides.'
                );

            } else {

                setErreur(
                    "Une erreur est survenue pendant l'enregistrement."
                );
            }

        } finally {

            setChargement(false);
        }
    };

    const fermerApresSucces = () => {

        dispatch(showmodal(false));

        setValPrevendeur('');
        setValMarque('');
        setValObjectif('');
        setErreur('');
    };

    return (
        <Dialog
            open={objectifData.modalShow}
            onClose={fermer}
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle>
                Ajouter un objectif commercial
            </DialogTitle>

            {chargement && <LinearProgress />}

            <DialogContent>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        pt: 2
                    }}
                >

                    {/* Prévendeur */}

                    <FormControl fullWidth>
                        <InputLabel>
                            Prévendeur
                        </InputLabel>

                        <Select
                            value={valPrevendeur}
                            label="Prévendeur"
                            onChange={(event) => {
                                setValPrevendeur(
                                    event.target.value
                                );
                                setErreur('');
                            }}
                            disabled={chargement}
                        >

                            {objectifData.listPrevendeur.map(
                                (element) => (
                                    <MenuItem
                                        key={element._id}
                                        value={element._id}
                                    >
                                        {element.nom}
                                    </MenuItem>
                                )
                            )}

                        </Select>
                    </FormControl>

                    {/* Marque */}

                    <FormControl fullWidth>
                        <InputLabel>
                            Marque
                        </InputLabel>

                        <Select
                            value={valMarque}
                            label="Marque"
                            onChange={(event) => {
                                setValMarque(
                                    event.target.value
                                );
                                setErreur('');
                            }}
                            disabled={chargement}
                        >

                            {objectifData.listMarque.map(
                                (element) => (
                                    <MenuItem
                                        key={element._id}
                                        value={element._id}
                                    >
                                        {element.designation}
                                    </MenuItem>
                                )
                            )}

                        </Select>
                    </FormControl>

                    {/* Objectif CA */}

                    <TextField
                        fullWidth
                        label="Objectif chiffre d'affaires (DH)"
                        type="number"
                        value={valObjectif}
                        onChange={(event) => {
                            setValObjectif(event.target.value);
                            setErreur('');
                        }}
                        disabled={chargement}
                        inputProps={{
                            min: 0.01,
                            step: 0.01
                        }}
                    />

                    {/* Erreur */}

                    {erreur && (
                        <Alert severity="error">
                            {erreur}
                        </Alert>
                    )}

                </Box>

            </DialogContent>

            <DialogActions sx={{ p: 2 }}>

                <Button
                    onClick={fermer}
                    disabled={chargement}
                >
                    Annuler
                </Button>

                <Button
                    variant="contained"
                    onClick={enregistrer}
                    disabled={chargement}
                >
                    Enregistrer
                </Button>

            </DialogActions>

        </Dialog>
    );
}
